/**
 * Application-wide user-facing message constants.
 *
 * @remarks
 * Source: `docs/constant messages.txt` — keep in sync with that file.
 * Use these constants everywhere a message string is needed so wording
 * stays consistent across the app.
 */

// ─── Camera ───────────────────────────────────────────────────────────────────

/** Full camera-permission explanation shown in an alert or modal. */
export const CAM_PERM_MSG =
  "The Titanium app requires access to your device's camera. Please enable permissions in your device's settings.";

/** Short camera-permission toast — shown when the scanner fails to open. */
export const CAM_PERM_MSG_SHORT =
  'Unable to open camera. Please try again after sometime.';

// ─── Location ─────────────────────────────────────────────────────────────────

/** Full location-permission explanation. */
export const LOC_PERM_MSG =
  "The Titanium app requires access to your device's location. Please enable permissions in your device's settings.";

/** Short location-permission message. */
export const LOC_PERM_MSG_SHORT =
  'This app requires access to your device\'s location.';

/** Location permission required for affidavit actions. */
export const LOC_PERM_MSG_AFF =
  'The Titanium app requires your location to reject, sign & approve affidavits. ';

/** Prompt user to select their current location. */
export const LOC_PERM_MSG_ADD_AFF =
  'Please choose your current location to proceed.';

/**
 * Shown when the Google Maps Geocoding call in `AffidavitComponent.getAddress()`
 * times out (2026-08-04) — there is no retry affordance for location alone, so
 * the message points the officer at the one thing that actually re-triggers it.
 */
export const GEOCODE_TIMEOUT_MSG =
  'The location request timed out. Please reload this page and try again.';

/**
 * Shown when `AffidavitComponent.getAddress()` rejects for any reason OTHER
 * than the timeout above — e.g. `net::ERR_INTERNET_DISCONNECTED`, a DNS
 * failure, or any other immediate network-level rejection (2026-08-04).
 */
export const GEOCODE_FAILED_MSG =
  'Unable to fetch location. Please check your connection and try again.';

/**
 * Shown when applying a signature to a PDF affidavit fails specifically
 * because the process server's stored e-signature image is corrupted —
 * neither a valid PNG nor a valid JPEG (`PdfFillService.embedImage()`
 * throws `PdfSignatureImageError` for this case, 2026-08-26). Distinguished
 * from the generic {@link PDF_SIGN_FAILED_MSG} because this is the one
 * failure mode the officer can actually resolve themselves — re-saving
 * their signature produces fresh, valid image bytes.
 */
export const PDF_SIGNATURE_IMAGE_INVALID_MSG =
  'Your saved signature could not be used. Please save your signature again and try applying it once more.';

/**
 * Generic fallback shown when applying a signature to a PDF affidavit fails
 * for any reason OTHER than an invalid stored signature image (a corrupt or
 * unparsable PDF template, a pdf-lib/pdfjs failure, etc., 2026-08-26) —
 * there is no officer-actionable next step for these, unlike the
 * signature-specific message above.
 */
export const PDF_SIGN_FAILED_MSG =
  'Unable to apply signature to this document.';

/**
 * Shown when a captured/uploaded attempt photo is rejected because its own
 * EXIF metadata contains no GPS coordinates (MOB-295) and, on native, the
 * device-GPS-embed fallback (see `PhotoService.embedDeviceGps()`) also
 * failed to acquire a position. Shown via `ToastService.warning()` from
 * `PhotoService.capture()`.
 */
export const PHOTO_NO_GPS_MSG =
  'Photo rejected. GPS location data was not detected. Please enable Location Services and retake or re-upload the photo.';

/**
 * Shown (non-blocking) on native when Photos-library access is `'limited'`
 * — the access tier where iOS redacts GPS from the raw photo data this app
 * reads, even for a genuinely geotagged gallery photo (MOB-295 follow-up).
 * Capture still proceeds either way; this only nudges the officer toward
 * better accuracy. Shown via `ToastService.info()` from
 * `PhotoService.ensurePhotosPermission()`.
 */
export const PHOTO_LIMITED_ACCESS_MSG =
  'For accurate photo locations, enable Full Access to Photos in your device Settings.';

/**
 * Shown (blocking — capture returns without opening the picker) on native
 * when Photos-library access is permanently `'denied'` and the officer
 * tapped "From Photos" specifically (MOB-304). Unlike
 * {@link PHOTO_LIMITED_ACCESS_MSG}, `'denied'` means the OS will not show
 * its own permission dialog again, so an app-level toast is the only way
 * left to tell the officer why nothing happened and what to do about it.
 * Shown via `ToastService.warning()` from `PhotoService.capture()`.
 */
export const PHOTO_LIBRARY_PERM_MSG =
  "Photos access is required to add a photo from your library. Please enable it in your device's settings.";

// ─── Upqueue ──────────────────────────────────────────────────────────────────

/**
 * Shown when `UpqueueService` cannot persist the offline queue to device
 * storage — in practice a `QuotaExceededError` from the web/PWA
 * `localStorage` backing store, whose per-origin budget (~5 MB) the queue's
 * base64 photo payloads can exhaust (2026-08-05).
 *
 * @remarks
 * The submission is deliberately kept in the in-memory queue so an immediate
 * online transmit can still succeed — see
 * `UpqueueService.persistOrWarn()`'s doc comment for why the write failure is
 * NOT rolled back. Shown via `ToastService.errorUntilDismissed()`: the
 * officer must acknowledge it, since the consequence (field data lost if the
 * app is closed or reloaded before the upload completes) is theirs to act on.
 */
export const UPQUEUE_PERSIST_FAILED_MSG =
  'This submission could not be saved to your device. Upload it now from the Upqueue screen — it will be lost if you close or reload the app.';

// ─── Jobs ─────────────────────────────────────────────────────────────────────

/**
 * Shown when the job the officer is actively viewing (attempt log, document
 * verification) disappears from their assigned job list mid-session — e.g.
 * reassigned to another process server. Paired with a redirect back to
 * `/jobs`; see `AttemptLogComponent`/`VerifyComponent`'s job-removal effect.
 * Shown via `ToastService.errorUntilDismissed()` — this toast must be
 * explicitly acknowledged by the officer (tap its close button), not
 * auto-dismiss, since it explains why they were just redirected away.
 *
 * @param jobNum - The job's sequential number (`Job.jobNum`), captured
 *   *before* it disappeared from `openJobs` (by the time this message is
 *   shown, `JobsService.getJob()` can no longer resolve it).
 */
export function jobUnassignedMsg(jobNum: string): string {
  return `Job #${jobNum} has been unassigned and is no longer in your job list.`;
}

/**
 * Shown for any print/email failure in the Print Queue (`PrintQueueComponent.startPrint()`)
 * — deliberately generic rather than surfacing the raw thrown error (e.g.
 * "Network error", "Failed to email documents"), which isn't actionable for
 * the officer.
 */
export const PRINT_FAILURE_MESSAGE =
  'Print document request failed. Please try again or report the issue via text to Field Operations.';

// ─── Data / records ───────────────────────────────────────────────────────────

/** No records returned from a query. */
export const NO_REC_MSG = 'No record found!';

/** Job data unavailable — user should pick a different job. */
export const DATA_UNAVAIL_MSG =
  'Data is not available for this job. Please select another job for Attempt/Completion.';

// ─── System errors ────────────────────────────────────────────────────────────

/** Generic system error — directs user to report via text. */
export const UNABLE_TO_PROCESS =
  'Unable to process your request due to a system issue. Please report via text to Field Operations.';

/** System error when signed affidavit was queued but submission failed. */
export const UNABLE_TO_PROCESS_SIGNED_UPQUEUE =
  'Unable to process your request due to a system issue. Please report via text to Field Operations but your signed affidavit has been added to upqueue.';

/** System error when rejected affidavit was queued but submission failed. */
export const UNABLE_TO_PROCESS_REJECTED_UPQUEUE =
  'Unable to process your request due to a system issue. Please report via text to Field Operations but your rejected affidavit has been added to upqueue.';

// ─── Offline ──────────────────────────────────────────────────────────────────

/** User is offline — generic. */
export const OFFLINE_MESSAGE =
  'You are currently offline. Please check your internet connection.';

/** User is offline and a signed affidavit was queued. */
export const OFFLINE_MESSAGE_SIGNED_UPQUEUE =
  'You are currently offline. Please check your internet connection and signed affidavit has been added to upqueue.';

/** User is offline and a rejected affidavit was queued. */
export const OFFLINE_MESSAGE_REJECTED_UPQUEUE =
  'You are currently offline. Please check your internet connection and signed affidavit has been added to upqueue.';

// ─── Affidavit ────────────────────────────────────────────────────────────────

/** Confirmation after signed affidavit is submitted. */
export const THANK_SIGNED_AFFIDAVIT =
  'Thank you! Your Signed Affidavit has been submitted.';

/** Confirmation after rejected affidavit is submitted. */
export const THANK_REJECTED_AFFIDAVIT =
  'Thank you! Your Rejected Affidavit has been submitted.';

// ─── E-signature ──────────────────────────────────────────────────────────────

/** Signature failed validation. */
export const ESIGN_NOT_VALID = 'Signature is not valid.';

/** User needs to create a new signature. */
export const ESIGN_CREATE = 'Please create a new signature.';

// ─── Storage keys ─────────────────────────────────────────────────────────────

/** SecureStorage key for cached app settings. */
export const APP_SETTINGS_STORAGE_KEY = 'app_settings';
